import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Seo from "@/components/Seo";
import Marquee from "@/components/Marquee";
import PinnedCard from "@/components/PinnedCard";
import EventModal from "@/components/EventModal";
import { NewsletterSection } from "@/components/NewsletterForm";
import { MaskedLine, FadeUp } from "@/components/Reveal";
import { Spark, Squiggle, ScribbleArrow, Starburst, PatchBadge } from "@/components/Doodles";
import { useContent } from "@/context/ContentContext";
import { useEvents } from "@/hooks/useEvents";
import { LilyPadSection, KidsTableSection, HelpBuildSection, InsideCollage, DonateSection, SoftOpeningSection, ResidentsSection, ValuesSection } from "@/components/HomeSections";

const Hero = () => {
    const { t } = useContent();
    const { scrollY } = useScroll();
    const ySlow = useTransform(scrollY, [0, 700], [0, 140]);
    const yMid = useTransform(scrollY, [0, 700], [0, 70]);
    const yFast = useTransform(scrollY, [0, 700], [0, -110]);

    return (
        <section data-testid="home-hero" className="relative flex min-h-svh flex-col overflow-hidden pt-32 sm:pt-40">
            <motion.div style={{ y: ySlow }} className="pointer-events-none absolute -right-10 top-24 hidden md:block" aria-hidden="true">
                <PatchBadge className="h-40 w-40 -rotate-12 text-brick" />
            </motion.div>
            <motion.div style={{ y: yFast }} className="pointer-events-none absolute left-[8%] top-[62%] hidden lg:block" aria-hidden="true">
                <Spark className="h-12 w-12 text-mustard" />
            </motion.div>
            <motion.div style={{ y: yMid }} className="pointer-events-none absolute bottom-24 right-[16%] hidden lg:block" aria-hidden="true">
                <Spark className="h-8 w-8 text-orange" />
            </motion.div>

            <div className="relative mx-auto w-full max-w-7xl px-5 pb-14 sm:px-10">
                <MaskedLine delay={0.05}>
                    <span className="inline-block border-2 border-ink bg-mustard px-3 py-1 font-display text-xs tracking-[0.2em] text-ink hard-shadow sm:text-sm -rotate-1">
                        {t("home.hero_kicker", "NOW FORMING · RICHLAND HILLS, TX")}
                    </span>
                </MaskedLine>

                <h1 className="mt-6 font-display leading-[0.86] tracking-wide text-ink" data-testid="home-hero-title">
                    <MaskedLine delay={0.15} className="text-[clamp(4.2rem,17vw,12.5rem)]">{t("home.hero_line1", "PANTHER")}</MaskedLine>
                    <MaskedLine delay={0.28} className="text-[clamp(4.2rem,17vw,12.5rem)] text-forest">{t("home.hero_line2", "CITY")}</MaskedLine>
                    <MaskedLine delay={0.41} className="text-[clamp(4.2rem,17vw,12.5rem)] text-orange">{t("home.hero_line3", "CLUBHOUSE")}</MaskedLine>
                </h1>

                <div className="mt-6 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
                    <div className="max-w-xl">
                        <MaskedLine delay={0.55}>
                            <span className="inline-block -rotate-2 font-hand text-3xl text-brick sm:text-4xl">
                                {t("home.hero_tagline", "A gathering place for weirdos.")}
                            </span>
                        </MaskedLine>
                        <FadeUp delay={0.7}>
                            <p className="mt-4 text-base leading-relaxed text-ink/80 sm:text-lg">
                                {t("home.hero_sub", "We're building a warm, creative, slightly weird place to gather, make things, learn stuff, work, host, and loiter with intent.")}
                            </p>
                        </FadeUp>
                        <FadeUp delay={0.82} className="mt-8 flex flex-wrap items-center gap-5">
                            <Link to="/visit" data-testid="hero-cta-visit" className="btn-stamp">
                                Come to the Clubhouse
                            </Link>
                            <a
                                href="#newsletter"
                                data-testid="hero-cta-newsletter"
                                className="group inline-flex items-center gap-2 font-display text-lg tracking-wider text-ink underline decoration-orange decoration-4 underline-offset-8 transition-colors hover:text-orange"
                            >
                                Get the newsletter
                                <ScribbleArrow className="h-8 w-10 text-orange transition-transform duration-300 group-hover:translate-x-1 group-hover:rotate-6" />
                            </a>
                        </FadeUp>
                    </div>
                    <FadeUp delay={0.9} className="hidden shrink-0 flex-col items-center gap-10 md:flex">
                        <Starburst className="h-36 w-36 text-brick" lines={["COMING", "SOON"]} />
                        <div className="relative -rotate-2 border-2 border-ink bg-paper p-2.5 hard-shadow">
                            <span className="tape-strip" aria-hidden="true" />
                            <img src={t("home.hero_photo", "/images/clubhouse-building.jpeg")} alt="The Panther City Clubhouse building at dusk, string lights glowing" className="h-44 w-56 border-2 border-ink object-cover" />
                        </div>
                    </FadeUp>
                </div>
            </div>
            <div className="mt-auto w-full">
                <Marquee />
            </div>
        </section>
    );
};

const Manifesto = () => {
    const { t } = useContent();
    const chapters = t("home.manifesto", []);
    return (
        <section data-testid="home-manifesto" className="px-5 py-16 sm:px-10 sm:py-24">
            <div className="mx-auto max-w-6xl">
                <FadeUp>
                    <p className="font-hand text-2xl text-orange -rotate-2 sm:text-3xl">the house rules</p>
                    <h2 className="mt-1 font-display text-4xl tracking-wide text-ink sm:text-5xl lg:text-6xl">WHY WE'RE BUILDING THIS</h2>
                    <Squiggle className="mt-4 h-5 w-48 text-mustard" />
                </FadeUp>
                <div className="mt-12 grid gap-6 sm:grid-cols-2">
                    {chapters.map((c, i) => (
                        <FadeUp key={c.num} delay={i * 0.08} className={i % 2 === 1 ? "sm:mt-12" : ""}>
                            <div
                                data-testid={`manifesto-chapter-${c.num}`}
                                className="group border-2 border-ink bg-paper p-7 hard-shadow transition-transform duration-200 hover:-rotate-1 sm:p-9"
                            >
                                <span className="font-display text-5xl text-mustard transition-colors duration-200 group-hover:text-orange">{c.num}</span>
                                <h3 className="mt-2 font-display text-2xl tracking-wide text-ink sm:text-3xl">{c.title}</h3>
                                <p className="mt-3 leading-relaxed text-ink/75">{c.body}</p>
                            </div>
                        </FadeUp>
                    ))}
                </div>
            </div>
        </section>
    );
};

const HappeningTeaser = () => {
    const { t } = useContent();
    const { events } = useEvents();
    const [selected, setSelected] = useState(null);
    return (
        <section data-testid="home-happening" className="border-y-2 border-ink bg-forest px-5 py-16 sm:px-10 sm:py-24">
            <div className="mx-auto max-w-6xl">
                <FadeUp className="flex flex-wrap items-end justify-between gap-6">
                    <div>
                        <p className="font-hand text-2xl text-mustard -rotate-2 sm:text-3xl">fresh on the corkboard</p>
                        <h2 className="mt-1 font-display text-4xl tracking-wide text-cream sm:text-5xl lg:text-6xl">WHAT'S HAPPENING</h2>
                    </div>
                    <Link
                        to="/whats-happening"
                        data-testid="home-happening-more-link"
                        className="font-display text-lg tracking-wider text-mustard underline decoration-2 underline-offset-8 hover:text-cream"
                    >
                        See All Events →
                    </Link>
                </FadeUp>
                <div className="mt-12 grid gap-8 md:grid-cols-3">
                    {(events || []).slice(0, 3).map((e, i) => (
                        <FadeUp key={e.id} delay={i * 0.1}>
                            <PinnedCard event={e} rotate={["-1.5deg", "1deg", "-0.75deg"][i % 3]} onSelect={setSelected} />
                        </FadeUp>
                    ))}
                    {events && events.length === 0 && (
                        <p data-testid="home-happening-empty" className="font-hand text-2xl text-cream/80">
                            Nothing pinned up just yet. Check back soon.
                        </p>
                    )}
                </div>
                <FadeUp delay={0.2}>
                    <p className="mt-10 text-cream/80">{t("happening.intro", "")}</p>
                </FadeUp>
            </div>
            <EventModal event={selected} onClose={() => setSelected(null)} />
        </section>
    );
};

const MembershipTeaser = () => (
    <section data-testid="home-membership-teaser" className="border-b-2 border-ink bg-mustard px-5 py-16 sm:px-10 sm:py-20">
        <div className="mx-auto flex max-w-6xl flex-col items-start gap-8 lg:flex-row lg:items-center lg:justify-between">
            <FadeUp>
                <p className="font-hand text-2xl text-brick -rotate-2 sm:text-3xl">cheap fun, expensive vibes</p>
                <h2 className="mt-1 max-w-xl font-display text-4xl leading-[0.95] tracking-wide text-ink sm:text-5xl lg:text-6xl">
                    FOUNDING MEMBERS GET THE GOOD CHAIR. FOREVER.
                </h2>
                <p className="mt-4 max-w-xl text-ink/80">
                    Memberships keep the lights on and the coffee hot. Founding rates vanish the day we open — like a casserole at a potluck.
                </p>
            </FadeUp>
            <FadeUp delay={0.15}>
                <Link to="/membership" data-testid="home-membership-cta" className="btn-stamp !bg-brick">
                    See Memberships
                </Link>
            </FadeUp>
        </div>
    </section>
);

const Closing = () => {
    const { t } = useContent();
    return (
        <section data-testid="home-closing" className="px-5 py-16 text-center sm:px-10 sm:py-24">
            <FadeUp className="mx-auto max-w-3xl">
                <Starburst className="mx-auto h-28 w-28 text-orange" lines={["EST.", "2026"]} />
                <h2 className="mt-6 font-display text-4xl tracking-wide text-ink sm:text-5xl lg:text-6xl">
                    {t("home.closing_title", "Be first through the door.")}
                </h2>
                <p className="mx-auto mt-4 max-w-xl text-ink/80">{t("home.closing_body", "")}</p>
            </FadeUp>
        </section>
    );
};

const Home = () => (
    <>
        <Seo
            title=""
            description="Panther City Clubhouse — a gathering place for weirdos. A community clubhouse and third space at 7264 Glenview Dr, Richland Hills, TX. Soft opening Friday, November 13, 2026."
        />
        <Hero />
        <SoftOpeningSection />
        <HappeningTeaser />
        <ResidentsSection />
        <InsideCollage />
        <MembershipTeaser />
        <Closing />
        <HelpBuildSection />
        <DonateSection />
        <Manifesto />
        <ValuesSection />
        <LilyPadSection />
        <KidsTableSection />
        <div id="newsletter">
            <NewsletterSection idPrefix="home-newsletter" />
        </div>
        <Marquee invert />
    </>
);

export default Home;
